import allImgPaths from 'assets/images';

import AddSource from 'components/common/modals/AddSource';
import Toast from 'components/common/Toast';
import useAppState from 'context/useAppState';
import { usePagination } from 'hooks/paginate/usePagination';
import { ClaimsTableShimmerLoader } from 'pages/loaders';
import React, { useCallback } from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getClaimChartById, updateClaimChart, deleteClaimChart, updateHeatmap, deleteHeatmap } from 'services/project';
import { formatDate } from 'utils/function';
import ClaimChartDownload from './ClaimChartDownload';
import ClaimChartDownloadLoader from 'pages/loaders/ClaimChartDownloadLoader';

function ClaimTable({ claimData, projectId, isClaimTableLoading, handleSort, sortConfig, onRefresh }: { claimData: any[], projectId: string, isClaimTableLoading: boolean, handleSort:any, sortConfig:any, onRefresh?: () => void }) {
    const {
        showClaimChartDownload,
        isDownloadClicked,
        newData,
        imagesLoaded,
        setImagesLoaded,
        handleDownload,
        claimChartData,
        currentMessageIndex,
        loaderMessages
      } = useAppState('claimChartDownload');

    const claimColumns: any[] = [
        {
            name: 'Vijil Chart Name',
            ref: 'name',
            hide: false,
            position: 1,
        },
        {
            name: 'Created by',
            ref: 'createdBy',
            hide: false,
            position: 2,
        },
        {
            name: 'Last Update',
            ref: 'updatedAt',
            hide: false,
            position: 3,
        },
        {
            name: 'Actions',
            ref: 'action',
            hide: false,
            position: 4,
        },
    ];

    const [paginatedData, setPaginatedData] = useState<any[]>([]);
    const [selectedItems, setSelectedItems] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);
    const [sortOrder, setSortOrder] = useState('asc');
    const [arrowUp, setArrowUp] = useState(false);
    
    // Modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedVijilChart, setSelectedVijilChart] = useState<any>(null);
    const [editName, setEditName] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // Heatmap modal state
    const [showEditHeatmapModal, setShowEditHeatmapModal] = useState(false);
    const [showDeleteHeatmapModal, setShowDeleteHeatmapModal] = useState(false);
    const [selectedHeatmap, setSelectedHeatmap] = useState<any>(null);
    const [editHeatmapName, setEditHeatmapName] = useState('');
    const [isUpdatingHeatmap, setIsUpdatingHeatmap] = useState(false);
    const [isDeletingHeatmap, setIsDeletingHeatmap] = useState(false);

    const navigate = useNavigate();
    const totalItems = claimData.length;
    const itemsPerPage = 5;
    const { currentPage, totalPages, startItem, endItem, nextPage, prevPage, currentData } = usePagination({
        totalItems,
        itemsPerPage,
    });
    const createColumnClassName = (field: string) => {
        return 'bg-[#fff] border border-[#E6E6E6] border-r-0 border-y py-3 px-6 border-t-0';
    }

    const handleSelectAll = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSelectAll(e.target.checked);
            if (e.target.checked) {
                console.log('hit all');

                // Select all items in the current page
                const currentPageIds = paginatedData.map((_, index) => index);
                setSelectedItems(currentPageIds);
            } else {
                // Deselect all items
                setSelectedItems([]);
            }
        },
        [paginatedData]
    );

    const handleSelectItem = useCallback(
        (index: number) => {
            setSelectedItems((prev) => {
                const newSelected = prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index];

                // Update selectAll state based on if all items in current page are selected
                setSelectAll(newSelected.length === paginatedData.length);

                return newSelected;
            });
        },
        [paginatedData]
    );

    const handlePrev = () => {
        prevPage();
        setPaginatedData(currentData(claimData));
    };
    const handleNext = () => {
        nextPage();
        setPaginatedData(currentData(claimData));
    };

    useEffect(() => {
        const newData = currentData(claimData);
        setPaginatedData(newData);
        setSelectedItems([]);
        setSelectAll(false);
    }, [currentPage]);


    const handleDownloadClick = async (claimChartId: string) => {
        try {
          const response = await getClaimChartById(claimChartId);
          const claimChartData = response.data;
          const APIData = claimChartData.claim_chart_data;
          handleDownload(APIData, claimChartData);
        } catch (error) {
          Toast.error("Error", "Failed to download document");
          console.error("Download error:", error);
        }
      };

    const handleEditClick = (row: any) => {
        setSelectedVijilChart(row);
        setEditName(row.name);
        setShowEditModal(true);
    };

    const handleDeleteClick = (row: any) => {
        setSelectedVijilChart(row);
        setShowDeleteModal(true);
    };

    const handleUpdateVijilChart = async () => {
        if (!selectedVijilChart || !editName.trim()) return;
        
        setIsUpdating(true);
        try {
            await updateClaimChart(selectedVijilChart._id, editName.trim());
            Toast.success("Success", "Vijil Chart updated successfully");
            setShowEditModal(false);
            setSelectedVijilChart(null);
            setEditName('');
            onRefresh?.();
        } catch (error) {
            Toast.error("Error", "Failed to update Vijil Chart");
            console.error("Update error:", error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteVijilChart = async () => {
        if (!selectedVijilChart) return;
        
        setIsDeleting(true);
        try {
            await deleteClaimChart(selectedVijilChart._id);
            Toast.success("Success", "Vijil Chart deleted successfully");
            setShowDeleteModal(false);
            setSelectedVijilChart(null);
            onRefresh?.();
        } catch (error) {
            Toast.error("Error", "Failed to delete Vijil Chart");
            console.error("Delete error:", error);
        } finally {
            setIsDeleting(false);
        }
    };

    // Heatmap handlers
    const handleEditHeatmapClick = (row: any) => {
        setSelectedHeatmap(row);
        // Extract the display name (remove timestamp prefix and .xlsx extension)
        const displayName = row.heatmap_file_name?.replace(/\.xlsx$/, '').replace(/^\d+_/, '') || '';
        setEditHeatmapName(displayName);
        setShowEditHeatmapModal(true);
    };

    const handleDeleteHeatmapClick = (row: any) => {
        setSelectedHeatmap(row);
        setShowDeleteHeatmapModal(true);
    };

    const handleUpdateHeatmap = async () => {
        if (!selectedHeatmap || !editHeatmapName.trim()) return;
        
        setIsUpdatingHeatmap(true);
        try {
            // Add back the .xlsx extension
            const newFileName = editHeatmapName.trim().endsWith('.xlsx') 
                ? editHeatmapName.trim() 
                : `${editHeatmapName.trim()}.xlsx`;
            await updateHeatmap(selectedHeatmap._id, newFileName);
            Toast.success("Success", "Heatmap updated successfully");
            setShowEditHeatmapModal(false);
            setSelectedHeatmap(null);
            setEditHeatmapName('');
            onRefresh?.();
        } catch (error) {
            Toast.error("Error", "Failed to update Heatmap");
            console.error("Update error:", error);
        } finally {
            setIsUpdatingHeatmap(false);
        }
    };

    const handleDeleteHeatmap = async () => {
        if (!selectedHeatmap) return;
        
        setIsDeletingHeatmap(true);
        try {
            await deleteHeatmap(selectedHeatmap._id);
            Toast.success("Success", "Heatmap deleted successfully");
            setShowDeleteHeatmapModal(false);
            setSelectedHeatmap(null);
            onRefresh?.();
        } catch (error) {
            Toast.error("Error", "Failed to delete Heatmap");
            console.error("Delete error:", error);
        } finally {
            setIsDeletingHeatmap(false);
        }
    };

    return (

        <div className='bg-white h-[calc(100vh_-_165px)] rounded-lg'>
            {isClaimTableLoading ? <ClaimsTableShimmerLoader /> :
                <>
                    {/* Tabs Head */}
                    <div className='flex bg-white sticky top-0 z-10 border-t border-[#F1F1F1] rounded-tl-lg rounded-tr-lg'>
                        {claimColumns?.length > 0 && claimData.length > 0 &&
                            claimColumns.map((column: any, columnIndex: number) => (
                                <React.Fragment key={columnIndex}>
                                    {
                                        <>
                                            {/* Vijil Chart Name */}
                                            {column.ref === 'name' && (
                                                <div className='w-[30%] flex-shrink-0 border-b border-[#F1F1F1] border-t-0 pt-1.5 pb-2 px-6'>
                                                    <div className='flex items-center gap-x-1 cursor-pointer'>
                                                        <span className='font-interMedium text-sm text-[#0A090B]'>
                                                            {column.name}
                                                        </span>
                                                        <img
                                                                onClick={() => handleSort('name', 'vijilChart')}
                                                                className={`w-4 cursor-pointer transform transition-transform duration-300 ${sortConfig['vijilChart']?.name === 'asc' ? 'rotate-0' : 'rotate-180'}`}
                                                                src={allImgPaths.arrowUp3}
                                                                alt=""
                                                            />
                                                    </div>
                                                </div>
                                            )}
                                            {/* Created by */}
                                            {column.ref === 'createdBy' && (
                                                <div className='w-[30%] flex-shrink-0 border-b border-[#F1F1F1] border-t-0 pt-1.5 pb-2 px-6'>
                                                    <div className='flex items-center justify-start gap-x-2 pt-0.5'>
                                                        <span className='cursor-pointer font-interMedium text-sm text-[#0A090B]' >
                                                            {column.name}
                                                        </span>
                                                        <img
                                                                onClick={() => handleSort('createdAt', 'vijilChart')}
                                                                className={`w-4 cursor-pointer transform transition-transform duration-300 ${sortConfig['vijilChart']?.createdAt === 'asc' ? 'rotate-0' : 'rotate-180'}`}
                                                                src={allImgPaths.arrowUp3}
                                                                alt=""
                                                            />
                                                    </div>
                                                </div>
                                            )}
                                            {/* Last Update */}
                                            {column.ref === 'updatedAt' && (
                                                <div className='w-[30%] flex-shrink-0 border-b border-[#F1F1F1] border-t-0 pt-1.5 pb-2 px-6'>
                                                    <div className="flex items-center justify-start gap-x-2 pt-0.5">
                                                        <span className='cursor-pointer font-interMedium text-sm text-[#0A090B]'>
                                                            {column.name}
                                                        </span>
                                                        <img
                                                                onClick={() => handleSort('updatedAt', 'vijilChart')}
                                                                className={`w-4 cursor-pointer transform transition-transform duration-300 ${sortConfig['vijilChart']?.updatedAt === 'asc' ? 'rotate-0' : 'rotate-180'}`}
                                                                src={allImgPaths.arrowUp3}
                                                                alt=""
                                                            />
                                                    </div>
                                                </div>
                                            )}
                                            {/* Action */}
                                            {column.ref === 'action' &&
                                                <div className='w-[10%] flex flex-shrink-0 border-b border-[#F1F1F1] border-t-0 pt-1.5 pb-2 px-6 justify-center'>
                                                    <div className='font-interMedium text-sm text-[#0A090B] inline-block'>{column.name}</div>
                                                </div>
                                            }
                                        </>
                                    }
                                </React.Fragment>
                            ))}
                    </div>

                    {/* Body */}
                    {true && (
                        <div className={'max-h-[calc(100vh_-_205px)] overflow-y-auto'}>
                                    {claimData.length > 0 &&
                                claimData.map((row: any, rowIndex: number) => (
                                    <React.Fragment key={rowIndex}>
                                        {/* Main Vijil Chart Row - only show if chart is active */}
                                        {row['is_chart_active'] !== false && (
                                        <div className={`flex`}>
                                            {claimColumns?.length > 0 &&
                                                claimColumns.map((column: any, columnIndex: number) => (
                                                    <React.Fragment key={columnIndex}>
                                                        {column.ref == 'name' && (
                                                            <div className={`w-[30%] flex-shrink-0 border-b border-[#F1F1F1] border-t-0 pt-2 pb-2 px-6 table-row !justify-start`}>
                                                                <Link to={`/project/${projectId}/claim-chart/${row['_id']}`}>
                                                                    <span className='font-interMedium text-sm pb-0.5 hover:text-[#113B98] text-[#1751D0]'>
                                                                        {row[column.ref]}
                                                                    </span>
                                                                </Link>
                                                            </div>
                                                        )}

                                                        {column.ref == 'createdBy' && (
                                                            <div className={`w-[30%] flex-shrink-0 border-b border-[#F1F1F1] border-t-0 pt-2 pb-2 px-6 table-row !justify-start`}>
                                                                <p className='text-sm text-[#4F4D55] font-interRegular'> {formatDate(row['createdAt'])} </p>
                                                                <p className='text-sm text-[#ADACB0] font-interRegular'> {row['created_by']['name']} </p>
                                                            </div>
                                                        )}

                                                        {column.ref == 'updatedAt' && (
                                                            <div className={`w-[30%] flex-shrink-0 border-b border-[#F1F1F1] border-t-0 pt-2 pb-2 px-6 table-row !justify-start`}>
                                                                <p className='text-sm text-[#4F4D55] font-interRegular'> {formatDate(row['updatedAt'])} </p>
                                                                <p className='text-sm text-[#ADACB0] font-interRegular'> {row['updated_by']['name']} </p>
                                                            </div>
                                                        )}

                                                        {column.ref === 'action' && (
                                                            <div className={`w-[10%] flex-shrink-0 border-b border-[#F1F1F1] border-t-0 py-2 px-6 flex items-center justify-center gap-x-1 relative`}>
                                                                <img
                                                                    src={allImgPaths.downloadIcon2}
                                                                    alt="download"
                                                                    className="inline-block cursor-pointer on-hover-bg-grey w-7 p-1 rounded"
                                                                    onClick={() => handleDownloadClick(row['_id'])}
                                                                    title="Download"
                                                                />
                                                                <img
                                                                    src={allImgPaths.editIcon}
                                                                    alt="rename"
                                                                    className="inline-block cursor-pointer on-hover-bg-grey w-7 p-1 rounded"
                                                                    onClick={() => handleEditClick(row)}
                                                                    title="Rename"
                                                                />
                                                                <img
                                                                    src={allImgPaths.trashIcon}
                                                                    alt="delete"
                                                                    className="inline-block cursor-pointer on-hover-bg-grey w-7 p-1 rounded"
                                                                    onClick={() => handleDeleteClick(row)}
                                                                    title="Delete"
                                                                />
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                        </div>
                                        )}
                                        
                                        {/* Heatmap Sub-row (if exists) */}
                                        {row['heatmap_file_name'] && (
                                            <div className={`flex`}>
                                                {claimColumns?.length > 0 &&
                                                    claimColumns.map((column: any, columnIndex: number) => (
                                                        <React.Fragment key={columnIndex}>
                                                            {column.ref == 'name' && (
                                                                <div className={`w-[30%] flex-shrink-0 border-b border-[#F1F1F1] border-t-0 pt-2 pb-2 px-6 table-row !justify-start`}>
                                                                    <span className='font-interMedium text-sm pb-0.5 text-[#1751D0]'>
                                                                        {row['heatmap_file_name'].replace(/\.xlsx$/, '').replace(/^\d+_/, '')}
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {column.ref == 'createdBy' && (
                                                                <div className={`w-[30%] flex-shrink-0 border-b border-[#F1F1F1] border-t-0 pt-2 pb-2 px-6`}>
                                                                    <p className='text-sm text-[#4F4D55] font-interRegular'> {formatDate(row['createdAt'])} </p>
                                                                    <p className='text-sm text-[#ADACB0] font-interRegular'> {row['created_by']['name']} </p>
                                                                </div>
                                                            )}

                                                            {column.ref == 'updatedAt' && (
                                                                <div className={`w-[30%] flex-shrink-0 border-b border-[#F1F1F1] border-t-0 pt-2 pb-2 px-6`}>
                                                                    <p className='text-sm text-[#4F4D55] font-interRegular'> {formatDate(row['updatedAt'])} </p>
                                                                    <p className='text-sm text-[#ADACB0] font-interRegular'> {row['updated_by']['name']} </p>
                                                                </div>
                                                            )}

                                                            {column.ref === 'action' && (
                                                                <div className={`w-[10%] flex-shrink-0 border-b border-[#F1F1F1] border-t-0 py-2 px-6 flex items-center justify-center gap-x-1 relative`}>
                                                                    <a
                                                                        href={row['heatmap_file_url']}
                                                                        download={row['heatmap_file_name']}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title="Download"
                                                                    >
                                                                        <img
                                                                            src={allImgPaths.downloadIcon2}
                                                                            alt="download"
                                                                            className="inline-block cursor-pointer on-hover-bg-grey w-7 p-1 rounded"
                                                                        />
                                                                    </a>
                                                                    <img
                                                                        src={allImgPaths.editIcon}
                                                                        alt="rename"
                                                                        className="inline-block cursor-pointer on-hover-bg-grey w-7 p-1 rounded"
                                                                        onClick={() => handleEditHeatmapClick(row)}
                                                                        title="Rename"
                                                                    />
                                                                    <img
                                                                        src={allImgPaths.trashIcon}
                                                                        alt="delete"
                                                                        className="inline-block cursor-pointer on-hover-bg-grey w-7 p-1 rounded"
                                                                        onClick={() => handleDeleteHeatmapClick(row)}
                                                                        title="Delete"
                                                                    />
                                                                </div>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}
                            {claimData.length === 0 && <div className="flex justify-center items-center h-[calc(100vh_-_356px)]">
                                <div className="text-center">
                                    <img className="mb-2.5 ml-auto mr-auto" src={allImgPaths.searchIcon3} alt="" />
                                    <p className="font-interSemiBold text-base text-[#101828]">No data found</p>
                                    <p className="font-interRegular text-sm text-[#475467]">Please add patents, files, URLs, publications to <br /> compare with your source patent</p>
                                </div>
                            </div>}
                        </div>
                    )}
                </>
            }

        {showClaimChartDownload && (
            <div 
            className="absolute top-[-9999px] left-[-9999px]"
            aria-hidden="true"
            id="hidden-download-container"
          >
            <ClaimChartDownload
              newData={newData}
              claimChartAPIData={claimChartData}
              imagesLoaded={imagesLoaded}
              setImagesLoaded={setImagesLoaded}
            />
          </div>
          )}

      {isDownloadClicked && <ClaimChartDownloadLoader loaderMessages={loaderMessages} currentMessageIndex={currentMessageIndex} />}

            {/* Edit Vijil Chart Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                    <div className="bg-white rounded-lg w-[400px] p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-interSemiBold text-[#101828]">Edit Vijil Chart</h3>
                            <img
                                onClick={() => {
                                    setShowEditModal(false);
                                    setSelectedVijilChart(null);
                                    setEditName('');
                                }}
                                className="cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1"
                                src={allImgPaths.closeIcon}
                                alt="close"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-interMedium text-[#344054] mb-1.5">
                                Name
                            </label>
                            <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="w-full border border-[#D0D5DD] rounded-lg px-3.5 py-2.5 text-sm font-interRegular text-[#101828] focus:outline-none focus:border-[#1751D0]"
                                placeholder="Enter Vijil Chart name"
                            />
                        </div>
                        <div className="flex justify-end gap-x-3">
                            <button
                                onClick={() => {
                                    setShowEditModal(false);
                                    setSelectedVijilChart(null);
                                    setEditName('');
                                }}
                                className="px-4 py-2.5 border border-[#D0D5DD] rounded-lg text-sm font-interMedium text-[#344054] hover:bg-[#f2f4f7]"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateVijilChart}
                                disabled={isUpdating || !editName.trim()}
                                className="px-4 py-2.5 bg-[#1751D0] rounded-lg text-sm font-interMedium text-white hover:bg-[#113B98] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isUpdating ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Vijil Chart Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                    <div className="bg-white rounded-lg w-[400px] p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-interSemiBold text-[#101828]">Delete Vijil Chart</h3>
                            <img
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedVijilChart(null);
                                }}
                                className="cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1"
                                src={allImgPaths.closeIcon}
                                alt="close"
                            />
                        </div>
                        <p className="text-sm font-interRegular text-[#475467] mb-6">
                            Are you sure you want to delete "{selectedVijilChart?.name}"? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-x-3">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setSelectedVijilChart(null);
                                }}
                                className="px-4 py-2.5 border border-[#D0D5DD] rounded-lg text-sm font-interMedium text-[#344054] hover:bg-[#f2f4f7]"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteVijilChart}
                                disabled={isDeleting}
                                className="px-4 py-2.5 bg-[#D92D20] rounded-lg text-sm font-interMedium text-white hover:bg-[#B42318] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Heatmap Modal */}
            {showEditHeatmapModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                    <div className="bg-white rounded-lg w-[400px] p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-interSemiBold text-[#101828]">Edit Heatmap</h3>
                            <img
                                onClick={() => {
                                    setShowEditHeatmapModal(false);
                                    setSelectedHeatmap(null);
                                    setEditHeatmapName('');
                                }}
                                className="cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1"
                                src={allImgPaths.closeIcon}
                                alt="close"
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-interMedium text-[#344054] mb-1.5">
                                Name
                            </label>
                            <input
                                type="text"
                                value={editHeatmapName}
                                onChange={(e) => setEditHeatmapName(e.target.value)}
                                className="w-full border border-[#D0D5DD] rounded-lg px-3.5 py-2.5 text-sm font-interRegular text-[#101828] focus:outline-none focus:border-[#1751D0]"
                                placeholder="Enter Heatmap name"
                            />
                        </div>
                        <div className="flex justify-end gap-x-3">
                            <button
                                onClick={() => {
                                    setShowEditHeatmapModal(false);
                                    setSelectedHeatmap(null);
                                    setEditHeatmapName('');
                                }}
                                className="px-4 py-2.5 border border-[#D0D5DD] rounded-lg text-sm font-interMedium text-[#344054] hover:bg-[#f2f4f7]"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateHeatmap}
                                disabled={isUpdatingHeatmap || !editHeatmapName.trim()}
                                className="px-4 py-2.5 bg-[#1751D0] rounded-lg text-sm font-interMedium text-white hover:bg-[#113B98] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isUpdatingHeatmap ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Heatmap Modal */}
            {showDeleteHeatmapModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                    <div className="bg-white rounded-lg w-[400px] p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-interSemiBold text-[#101828]">Delete Heatmap</h3>
                            <img
                                onClick={() => {
                                    setShowDeleteHeatmapModal(false);
                                    setSelectedHeatmap(null);
                                }}
                                className="cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1"
                                src={allImgPaths.closeIcon}
                                alt="close"
                            />
                        </div>
                        <p className="text-sm font-interRegular text-[#475467] mb-6">
                            Are you sure you want to delete the heatmap "{selectedHeatmap?.heatmap_file_name?.replace(/\.xlsx$/, '').replace(/^\d+_/, '')}"? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-x-3">
                            <button
                                onClick={() => {
                                    setShowDeleteHeatmapModal(false);
                                    setSelectedHeatmap(null);
                                }}
                                className="px-4 py-2.5 border border-[#D0D5DD] rounded-lg text-sm font-interMedium text-[#344054] hover:bg-[#f2f4f7]"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteHeatmap}
                                disabled={isDeletingHeatmap}
                                className="px-4 py-2.5 bg-[#D92D20] rounded-lg text-sm font-interMedium text-white hover:bg-[#B42318] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isDeletingHeatmap ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
export default ClaimTable;
